import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  BarPlot,
  ChartContainer,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
} from "@mui/x-charts";

import {
  defaultColorPalette,
  getCartesianChartData,
  cartesianChartStyling,
  makeAbbrevValueFormatter,
  buildFullByAbbrevMap,
  makeNumericValueFormatter,
} from "./utils";

type ComboSeriesType = "bar" | "line";

interface ComboGraphProps {
  children?: React.ReactNode;
  content: Array<{ [key: string]: any }>;
  xAxisKey: string;
  yAxisKey: string;
  seriesTypes: Record<string, ComboSeriesType>;
}

const ComboGraph: React.FC<ComboGraphProps> = ({
  content,
  xAxisKey,
  yAxisKey,
  seriesTypes,
}) => {
  const theme = useTheme();
  const colorPalette = [theme.palette.primary.main, ...defaultColorPalette];
  const comboLineColor = "#5A4B66";

  const { xLabel, yLabel, abbreviatedContent, series } = getCartesianChartData(
    content,
    xAxisKey,
    colorPalette,
    yAxisKey,
  );
  const chartSeries = series
    .filter((item) => seriesTypes[item.dataKey])
    .map((item) => ({
      ...item,
      type: seriesTypes[item.dataKey],
      color:
        seriesTypes[item.dataKey] === "line"
          ? comboLineColor
          : theme.palette.primary.main,
    }));

  const fullByAbbrev = React.useMemo(
    () => buildFullByAbbrevMap(abbreviatedContent, xAxisKey),
    [abbreviatedContent, xAxisKey],
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) setContainerWidth(cr.width);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const height = 350;
  const width = Math.max(320, containerWidth);

  const margin = {
    left: Math.max(20, width * 0.07),
    right: Math.max(10, width * 0.03),
    top: 30,
    bottom: Math.max(20, width * 0.1),
  };

  return (
    <div ref={containerRef} style={{ width: "100%", height }}>
      {width > 0 && (
        <ChartContainer
          dataset={abbreviatedContent}
          xAxis={[
            {
              id: "x-axis",
              scaleType: "band",
              dataKey: xAxisKey,
              label: xLabel,
              valueFormatter: makeAbbrevValueFormatter(fullByAbbrev),
            },
          ]}
          yAxis={[
            {
              id: "y-axis",
              label: yLabel,
              valueFormatter: makeNumericValueFormatter(),
            },
          ]}
          series={chartSeries}
          height={height}
          width={width}
          margin={margin}
          {...cartesianChartStyling}
        >
          <BarPlot />
          <LinePlot />
          <ChartsXAxis axisId="x-axis" />
          <ChartsYAxis axisId="y-axis" />
          <ChartsTooltip />
        </ChartContainer>
      )}
    </div>
  );
};

export const validateComboGraph = (
  data: any[],
  xAxis?: string,
  seriesTypes?: Record<string, string>,
): boolean => {
  if (!Array.isArray(data) || data.length === 0) return false;
  if (typeof data[0] !== "object") return false;
  if (!seriesTypes || typeof seriesTypes !== "object") return false;

  const keys = Object.keys(data[0]);
  const seriesEntries = Object.entries(seriesTypes);
  const hasBar = seriesEntries.some(([, type]) => type === "bar");
  const hasLine = seriesEntries.some(([, type]) => type === "line");

  return (
    xAxis !== undefined &&
    keys.includes(xAxis) &&
    hasBar &&
    hasLine &&
    seriesEntries.every(
      ([key, type]) =>
        keys.includes(key) && (type === "bar" || type === "line"),
    )
  );
};

export default ComboGraph;
